import { useState, useEffect, useContext } from 'react';
import { AppContext, AjaxContext, EMPTY_STRING } from 'commons-ui';
import * as dashboardModel from '../../utils/model';
import { initialSegmentInputTextState, initialSegmentState } from '../../utils/constant';

const usePriorityList = () => {
	const { postData } = useContext(AjaxContext);
	const {
		showAlert,
		app: { appName }
	} = useContext(AppContext);
	const [segmentLists, setSegmentLists] = useState<dashboardModel.SegmentState>(initialSegmentState);

	const [segmentInputText, setSegmentInputText] = useState<dashboardModel.SegmentInputTextState>(initialSegmentInputTextState);
	const [showLoader, setShowLoader] = useState(false);

	const fetchPriorityData = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const result: { data: dashboardModel.IUserPrioritySegementResponse } = await postData({
				endpoint: '/marketing/dashboard/finance/getCtdPayoutSegmentsAndPriorityList',
				body: {},
				queryParams: {
					appName
				}
			});

			if (!result?.data?.success) throw new Error('Error in fetching the priority list!');

			const data = result?.data?.data;
			setSegmentLists((prev) => ({
				...prev,
				userCTDPayoutSegments: data?.userCTDPayoutSegments || [],
				ctdUserSegmentPriorityList: data?.ctdUserSegmentPriorityList || [],
				withdrawFeeSegmentList: data?.conversionConfig?.withdrawFeeSegmentList || [],
				ctdAmountLimitSegmentList: data?.conversionConfig?.ctdAmountLimitSegmentList || [],
				payoutAmountLimitSegmentList: data?.conversionConfig?.payoutAmountLimitSegmentList || [],
				coolOffSegmentList: data?.conversionConfig?.coolOffSegmentList || [],
				ctdCoolOffSegmentList: data?.conversionConfig?.ctdCoolOffSegmentList || [],
				payoutCoolOffSegmentList: data?.conversionConfig?.payoutCoolOffSegmentList || [],
				leaveIntentSegmentList: data?.conversionConfig?.leaveIntentSegmentList || []
			}));
			setSegmentInputText((prev) => ({
				...prev,
				userCTDPayoutSegments: (data?.userCTDPayoutSegments || [])?.join(','),
				ctdUserSegmentPriorityList: (data?.ctdUserSegmentPriorityList || [])?.join(','),
				withdrawFeeSegmentList: (data?.conversionConfig?.withdrawFeeSegmentList || [])?.join(','),
				ctdAmountLimitSegmentList: (data?.conversionConfig?.ctdAmountLimitSegmentList || [])?.join(','),
				payoutAmountLimitSegmentList: (data?.conversionConfig?.payoutAmountLimitSegmentList || [])?.join(','),
				coolOffSegmentList: (data?.conversionConfig?.coolOffSegmentList || [])?.join(','),
				ctdCoolOffSegmentList: (data?.conversionConfig?.ctdCoolOffSegmentList || [])?.join(','),
				payoutCoolOffSegmentList: (data?.conversionConfig?.payoutCoolOffSegmentList || [])?.join(','),
				leaveIntentSegmentList: (data?.conversionConfig?.leaveIntentSegmentList || [])?.join(',')
			}));
			return;
		} catch (error) {
			showAlert('Error in fetching the priority list', { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	useEffect(() => {
		fetchPriorityData();
	}, []);

	const validatePriorityList = (dataList: string): string[] => {
		return dataList
			?.split(',')
			?.filter((data: string) => data !== EMPTY_STRING)
			.map((item) => item.trim());
	};

	const submitSegmentPriority = async (name: keyof dashboardModel.SegmentInputTextState): Promise<void> => {
		try {
			setShowLoader(true);
			const result: { data: dashboardModel.RESPONSE } = await postData({
				endpoint: '/marketing/dashboard/finance/updateCTDConfigPriority',
				queryParams: {
					appName
				},
				body: {
					segmentProperty: name,
					priorityList: validatePriorityList(segmentInputText[name])
				}
			});
			if (!result?.data?.success) throw new Error('Error in updating the priority list!');
			setSegmentLists((prev) => ({
				...prev,
				[name]: validatePriorityList(segmentInputText[name])
			}));
			showAlert(`${name} updated successfully`, { type: 'success' });
		} catch (error: any) {
			showAlert(error.response.data.error ? error.response.data.error : `Error in updating ${name}`, { type: 'error' });
			throw error;
		} finally {
			setShowLoader(false);
		}
	};

	const handleInputChange = (event: { target: { value: string; name: string } }) => {
		setSegmentInputText((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};

	return {
		segmentLists,
		submitSegmentPriority,
		segmentInputText,
		handleInputChange,
		showLoader
	};
};

export default usePriorityList;
